/**
 * SEO component — uses React 19's native head support to inject per-page
 * meta tags, Open Graph, Twitter card, canonical, and optional JSON-LD.
 *
 * No external library needed. React 19 hoists <title>, <meta>, <link> from
 * anywhere in the tree to <head>.
 */
import {
  SITE_NAME,
  SITE_TAGLINE,
  SITE_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  buildUrl,
} from "../lib/site";

export default function SEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} · ${SITE_TAGLINE}`;
  const desc = description || SITE_DESCRIPTION;
  const canonical = buildUrl(path);
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data — Google reads this directly.
          We escape `<` to its Unicode form so the JSON payload can never
          accidentally close the surrounding <script> tag, even if a string
          inside the payload were ever to contain "</script>". This is the
          same approach Next.js' <Head> uses for inline JSON-LD. */}
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
          }}
        />
      )}
    </>
  );
}
